import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader,
  Plus,
  RefreshCcw,
} from 'lucide-react';

import { kanbanApiService } from '../../services/kanbanApi';
import { AdoptAsIncidentModal } from './AdoptAsIncidentModal';
import { cn } from '../../utils/cn';
import { SkeletonLoader } from '../SkeletonLoader';
import type { Incident, IncidentStatus } from '../../types/incident';

export interface CardIncidentsSectionProps {
  cardId: string;
  /** Default `card.title` for the adopt modal. */
  cardTitle?: string;
  /** Default `card.description` for the adopt modal. */
  cardDescription?: string | null;
  /** Default priority (1–10) for the adopt modal. */
  cardPriority?: number;
  canWrite: boolean;
  /**
   * Bumped by the parent (e.g. WorkspaceBoardView's WS handler) on
   * `card.incident_linked` / `card.incident_unlinked` events for this card.
   */
  refreshNonce?: number;
  /**
   * Called whenever the local incident list changes so the parent can re-evaluate
   * sync routing on this card's mutations.
   */
  onIncidentsChanged?: (incidents: Incident[]) => void;
  /** Compact rendering for tight side panels. */
  compact?: boolean;
}

const STATUS_CHIP: Record<IncidentStatus, string> = {
  active: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  critical: 'bg-red-500/15 text-red-300 border-red-500/25',
  resolved: 'bg-green-500/15 text-green-300 border-green-500/25',
  closed: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
  pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
  investigating: 'bg-purple-500/15 text-purple-300 border-purple-500/25',
};

/**
 * Renders the list of incidents bridged to a single kanban card via the new
 * Incident-Workspaces integration. Distinct from the legacy shallow link
 * (`KanbanCardLink` of type `incident`), which the card panel still surfaces in
 * its own "Links" subsection.
 */
export const CardIncidentsSection = ({
  cardId,
  cardTitle,
  cardDescription,
  cardPriority,
  canWrite,
  refreshNonce = 0,
  onIncidentsChanged,
  compact = false,
}: CardIncidentsSectionProps) => {
  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdopt, setShowAdopt] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(
    async (initial: boolean) => {
      if (!cardId) return;
      if (initial) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const list = await kanbanApiService.getIncidentsForCard(cardId);
        setIncidents(list);
        onIncidentsChanged?.(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load linked incidents');
      } finally {
        if (initial) setLoading(false);
        setRefreshing(false);
      }
    },
    [cardId, onIncidentsChanged],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load(false);
  }, [refreshNonce, load]);

  const count = incidents?.length ?? 0;

  return (
    <div
      className={cn(
        compact ? 'subcard p-3' : 'glass-card p-4',
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-glass-500 shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-glass-500 shrink-0" />
          )}
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <h4 className="text-sm font-semibold text-white truncate">Incidents</h4>
          <span className="badge badge-neutral">{count}</span>
          {refreshing && <Loader className="w-3 h-3 animate-spin text-glass-400" />}
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void load(false)}
            disabled={loading || refreshing}
            className="btn-icon btn-icon-sm"
            title="Refresh linked incidents"
            aria-label="Refresh linked incidents"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowAdopt(true)}
              className="glass-button-secondary btn-xs"
              title="Create a new incident from this card and bridge them."
            >
              <Plus className="w-3.5 h-3.5" />
              Adopt as incident
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <>
          {error && (
            <div className="alert alert-danger text-xs !py-2 mb-2" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="space-y-1.5" aria-busy="true" aria-label="Loading linked incidents">
              <SkeletonLoader className="h-9 w-full" />
              <SkeletonLoader className="h-9 w-full" />
            </div>
          ) : count === 0 ? (
            <p className="text-xs text-glass-500">
              No incidents bridged to this card yet.
              {canWrite && ' Use "Adopt as incident" to create one.'}
            </p>
          ) : (
            <div className="space-y-1.5">
              {(incidents ?? []).map((inc) => (
                <Link
                  key={inc.id}
                  to={`/incidents/${encodeURIComponent(inc.id)}`}
                  className="flex items-center gap-2 rounded-lg p-2 text-xs border border-glass-200/10 hover:bg-glass-200/10 hover:border-glass-200/20 transition-colors group"
                >
                  <span
                    className={cn(
                      'badge shrink-0 capitalize',
                      STATUS_CHIP[inc.status],
                    )}
                  >
                    {inc.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate group-hover:text-accent-300 transition-colors">
                      {inc.title}
                    </div>
                    <div className="text-[10px] text-glass-500 truncate tabular-nums">
                      P{inc.priority} · Created{' '}
                      {formatDistanceToNow(new Date(inc.created_at), { addSuffix: true })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}

      {showAdopt && (
        <AdoptAsIncidentModal
          isOpen={showAdopt}
          onClose={() => setShowAdopt(false)}
          cardId={cardId}
          defaultTitle={cardTitle}
          defaultDescription={cardDescription ?? undefined}
          defaultPriority={cardPriority}
          onSuccess={() => {
            // Refresh after a short delay to give the backend WS event time to fire.
            setShowAdopt(false);
            void load(false);
          }}
        />
      )}
    </div>
  );
};
